import React, { useState } from 'react';
import { Share2, Twitter, Facebook, Linkedin, Link as LinkIcon, X, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboardFallback } from '../lib/utils';

const shareLinksData = (shareUrl, shareTitle, shareText) => [
  {
    name: 'Twitter',
    icon: <Twitter size={20} />,
    href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    color: 'bg-[#1DA1F2] text-white hover:bg-[#1a8cd8]'
  },
  {
    name: 'Facebook',
    icon: <Facebook size={20} />,
    href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    color: 'bg-[#1877F2] text-white hover:bg-[#166fe5]'
  },
  {
    name: 'LinkedIn',
    icon: <Linkedin size={20} />,
    href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}&summary=${encodeURIComponent(shareText)}`,
    color: 'bg-[#0A66C2] text-white hover:bg-[#0958a8]'
  },
  {
    name: 'WhatsApp',
    icon: <MessageCircle size={20} />,
    href: `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    color: 'bg-[#25D366] text-white hover:bg-[#20bd5a]'
  }
];

const ShareModal = ({ setIsOpen, shareUrl, shareTitle, shareText, copyLink }) => {
  const links = shareLinksData(shareUrl, shareTitle, shareText);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-card border border-border shadow-lg p-6 rounded-xl w-full max-w-md m-4 relative animate-in zoom-in-95 duration-200">
        <button 
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors p-1"
        >
          <X size={20} />
        </button>
        
        <h3 className="text-xl font-bold mb-2">Share {shareTitle}</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Help other creators discover this free tool by sharing it with your network.
        </p>

        <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6">
          {links.map((link) => (
            <a
              key={link.name}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex flex-col items-center justify-center gap-2 p-4 rounded-lg transition-colors min-h-[80px] ${link.color}`}
            >
              {link.icon}
              <span className="text-xs font-medium">{link.name}</span>
            </a>
          ))}
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">Or copy link</span>
          </div>
        </div>

        <button
          onClick={copyLink}
          className="mt-6 w-full flex items-center justify-center gap-2 py-3.5 bg-secondary text-secondary-foreground hover:bg-muted transition-colors rounded-lg font-medium min-h-[48px]"
          data-testid="copy-share-link"
        >
          <LinkIcon size={18} />
          Copy Link
        </button>
      </div>
    </div>
  );
};

const ShareButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const shareUrl = window.location.origin;
  const shareTitle = "SuperTags";
  const shareText = "Check out this awesome free tool to generate YouTube tags and track video rankings!";

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      setIsOpen(true);
    }
  };

  const handleShareClick = () => {
    if (navigator.share && window.innerWidth < 768) {
      handleNativeShare();
    } else {
      setIsOpen(true);
    }
  };

  const copyLink = async () => {
    const success = await copyToClipboardFallback(shareUrl);
    if (success) {
      toast.success("Link copied to clipboard!");
    } else {
      toast.error("Failed to copy link");
    }
    setIsOpen(false);
  };

  return (
    <>
      <button
        onClick={handleShareClick}
        className="p-2.5 md:p-2 hover:bg-secondary transition-colors rounded-full border border-transparent hover:border-border min-h-[44px] min-w-[44px] flex items-center justify-center"
        title="Share this tool"
        aria-label="Share this tool"
        data-testid="share-button"
      >
        <Share2 size={20} />
      </button>

      {isOpen && (
        <ShareModal 
          setIsOpen={setIsOpen} 
          shareUrl={shareUrl} 
          shareTitle={shareTitle} 
          shareText={shareText} 
          copyLink={copyLink} 
        />
      )}
    </>
  );
};

export default ShareButton;
